import type { ChangeEvent } from 'react';
import styles from './BasicNumberInput.module.scss';
import classNames from 'classnames';
import { useEffect, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';

export type BasicNumberInputProps = {
  id: string;
  value: string;
  unit?: string;
  unitWidth?: number;
  onChange: (value: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  debouncedTime?: number;
  title?: string;
  validator?: (value: string) => boolean;
  onlyInt?: boolean;
  label?: string;
  allowEmpty?: boolean;
  errorMessage?: string;
};

export const BasicNumberInput = ({
  id,
  value,
  unit,
  unitWidth,
  onChange,
  onBlur,
  placeholder,
  debouncedTime,
  title,
  validator = () => true,
  onlyInt = false,
  label,
  allowEmpty = false,
  errorMessage,
}: BasicNumberInputProps) => {
  const [currentValue, setCurrentValue] = useState(value);
  const [showError, setShowError] = useState(false);

  const numberValidation = (inputValue: string) => {
    const test = onlyInt ? /^[1-9]+/.test(inputValue) : /^([0-9]+)?([,.])?([0-9]+)?$/.test(inputValue);
    return test;
  };

  const onInputValueChangeDebounce = useDebouncedCallback((inputValue: string) => {
    if (validator(inputValue) && numberValidation(inputValue)) {
      setShowError(false);
      if (inputValue !== value) {
        onChange(inputValue);
      }
    } else {
      setShowError(true);
    }
  }, debouncedTime || 1000);

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value;
    const numberValue = onlyInt
      ? input.replace(/\D/g, '')
      : input
          .replace(',', '.')
          .replace(/[^\d.]/g, '')
          .replace(/^(\d*\.?)|(\d*)\.?/g, '$1$2');
    setCurrentValue(numberValue);
  };

  const handleInputOnBlur = (inputValue: string) => {
    if (inputValue === '') {
      setCurrentValue(allowEmpty ? inputValue : value);
    } else {
      setCurrentValue(Number(inputValue).toString());
    }
  };

  useEffect(() => {
    setCurrentValue(value);
  }, [value]);

  useEffect(() => {
    onInputValueChangeDebounce(currentValue);
  }, [currentValue]);

  return (
    <div>
      <div className="flex items-center relative">
        <span
          className={classNames(
            'right-3',
            'absolute',
            unitWidth ? `w-${unitWidth}` : 'w-12',
            'pb-2',
            styles.unitText,
            'text-right'
          )}
        >
          {unit}
        </span>
        <span className={classNames('block', 'overflow-hidden', 'flex-grow')}>
          <input
            value={currentValue}
            placeholder={placeholder}
            data-testid={id}
            onChange={handleInputChange}
            onFocus={event => {
              event.target.select();
            }}
            onBlur={event => {
              const { value: inputValue } = event.target;
              onBlur && onBlur();
              handleInputOnBlur(inputValue);
            }}
            className={classNames(
              'w-full',
              'mb-2',
              'mr-4',
              'px-6',
              'py-2',
              'appearance-none',
              'focus:outline-none',
              'border',
              'border-gray-200',
              'rounded',
              styles.input
            )}
            type="text"
            title={title}
          />
        </span>
        {label && (
          <label htmlFor={id} className="ml-4">
            {label}
          </label>
        )}
      </div>

      {showError && errorMessage && (
        <span data-testid="error-message" className="text-xs mb-2 text-red-500">
          {errorMessage}
        </span>
      )}
    </div>
  );
};
